import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import { JSX, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdCategoryStore } from "./store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import Spinner from "@/components/ui/Spinner";
import PopoverMenu from "@/components/ui/Popeover";
import { RotateCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/Dialog";

export default function ViewArchivedPublishers(): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [publisherToRestore, setPublisherToRestore] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const navigate = useNavigate();
  const {
    getDeletedPublishers,
    adCategoryApiData,
    restorePublisher,
  } = useAdCategoryStore(
    useShallow((state) => ({
      getDeletedPublishers: state.getDeletedPublishers,
      adCategoryApiData: state.adCategoryApiData,
      restorePublisher: state.restorePublisher,
    })),
  );

  const setError = useGlobalStore((state) => state.setError);
  const showSuccess = useGlobalStore((state) => state.showSuccess);

  const [searchParams] = useSearchParams();
  const adCategoryId = searchParams.get("id");

  const fetchData = async () => {
    if (!adCategoryId) return;

    try {
      setIsLoading(true);
      await getDeletedPublishers(adCategoryId);
    } catch (error) {
      setError(error.message ?? "Failed to fetch archived publishers");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!publisherToRestore) return;

    try {
      await restorePublisher(publisherToRestore.id);
      showSuccess("Publisher restored successfully");
      await fetchData();
      setPublisherToRestore(null);
    } catch (error) {
      setError(error.message ?? "Failed to restore publisher");
    }
  };

  const createMenuItems = (publisherId: string, publisherName: string) => [
    {
      label: "Restore Publisher",
      icon: <RotateCcw className="w-4 h-4" />,
      onClick: () => setPublisherToRestore({ id: publisherId, name: publisherName }),
    },
  ];

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    }
    return (
      <svg
        className={`w-4 h-4 text-pink-500 transition-transform ${
          sortOrder === "desc" ? "rotate-180" : ""
        }`}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    );
  };

  const goPrevious = () => {
    setCurrentPage((page) => Math.max(page - 1, 1));
  };

  const goNext = () => {
    setCurrentPage((page) => Math.min(page + 1, totalPages));
  };

  const publishers = Array.isArray((adCategoryApiData as any)?.data?.data)
    ? (adCategoryApiData as any).data.data
    : [];

  const sortedPublishers = [...publishers].sort((a, b) => {
    let aValue: string;
    let bValue: string;

    switch (sortField) {
      case "name":
        aValue = a.name?.toLowerCase() || "";
        bValue = b.name?.toLowerCase() || "";
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedPublishers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPublishers = sortedPublishers.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    fetchData();
  }, []);

  if (!adCategoryId) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          Oops!!! The Ad Category ID is missing.
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return <Spinner className="flex justify-centers" />;
  }

  return (
    <Layout>
      <div>
        <div className="flex justify-between items-start mb-4">
          <h1 className="text-2xl font-bold">Archived Publishers</h1>
        </div>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => navigate(`/ad-categories/publishers?id=${adCategoryId}`)}
              className="text-pink-500 hover:text-pink-700 text-2xl"
              aria-label="Back to Ad Categories Dashboard"
            >
              ←
            </button>
            <span className="text-black text-xl">Back to Publishers</span>
          </div>
        </div>

        <hr className="border-[#EA33C0] mb-2" />

        <div className="pt-2">
          <table className="w-full mt-5">
            <thead className="border-b border-gray-300 text-gray-600/50 text-sm">
              <tr>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("name")}
                  >
                    Publisher Name
                    <SortIcon field="name" />
                  </div>
                </th>
                <th className="text-left py-2">Status</th>
                <th className="text-left py-2">Deleted At</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {currentPublishers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-gray-500">
                    No Archived Publishers Found
                  </td>
                </tr>
              ) : (
                currentPublishers.map((publisher: any) => (
                  <tr key={publisher.id} className="border-b border-gray-200">
                    <td className="py-4 font-semibold">{publisher.name}</td>
                    <td className="py-4">Archived</td>
                    <td className="py-4">
                      {publisher.deleted_at
                        ? new Date(publisher.deleted_at).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="text-right">
                      <PopoverMenu
                        items={createMenuItems(publisher.id, publisher.name)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end items-center mt-6 space-x-4 text-sm">
          <Button
            type="secondary"
            onClick={goPrevious}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
          <Button
            type="secondary"
            onClick={goNext}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={!!publisherToRestore}
        onOpenChange={() => setPublisherToRestore(null)}
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <div className="font-semibold text-xl text-center">
              Restore Publisher
            </div>
            <DialogDescription className="mt-4 text-center text-gray-600">
              Are you sure you want to restore{" "}
              <span className="font-semibold">{publisherToRestore?.name}</span>?
              This will make the publisher active again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-4 mt-6">
            <Button type="secondary" onClick={() => setPublisherToRestore(null)}>
              Cancel
            </Button>
            <Button
              type="primary"
              trailingIcon={<RotateCcw fontSize={18} />}
              onClick={handleRestore}
            >
              Yes, Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
